import { GameDocument, Resource } from '../../../types/game-document';

export const convertResourcesToBlobs = async (
  gameDocument: GameDocument,
  onProgress?: (current: number, total: number) => void,
  onDone?: () => void
): Promise<GameDocument> => {
  const urlRegex = /^https?:\/\//i;
  const srcRegex = /(https?:\/\/[^\s"')]+)/g;

  const allResources: Resource[] = [
    ...(gameDocument.resources ?? []),
    ...(gameDocument.resourcePacks?.flatMap((p) => p.resources ?? []) ?? [])
  ];

  // Gather resources that need converting
  const convertible = allResources.filter(
    (r) =>
      (r.type === 'image' ||
        r.type === 'video' ||
        r.type === 'text-html' ||
        r.type === 'text-md') &&
      typeof r.value === 'string' &&
      urlRegex.test(r.value)
  );

  const total = convertible.length;
  let current = 0;
  onProgress?.(current, total);

  const convertResource = async (res: Resource): Promise<Resource> => {
    try {
      if (
        (res.type === 'image' || res.type === 'video') &&
        typeof res.value === 'string' &&
        urlRegex.test(res.value)
      ) {
        const response = await fetch(res.value);
        const blob = await response.blob();
        current++;
        onProgress?.(current, total);

        return {
          ...res,
          value: blob
        };
      }

      if (
        (res.type === 'text-html' || res.type === 'text-md') &&
        typeof res.value === 'string'
      ) {
        const matches = [...res.value.matchAll(srcRegex)];
        const embeddedBlobs: { originalUrl: string; blob: Blob }[] = [];

        for (const match of matches) {
          try {
            const response = await fetch(match[1]);
            const blob = await response.blob();
            embeddedBlobs.push({ originalUrl: match[1], blob });
            current++;
            onProgress?.(current, total);
          } catch (e) {
            console.warn('Failed to fetch embedded URL:', match[1], e);
          }
        }

        return {
          ...res,
          embeddedBlobs
        };
      }

      return res;
    } catch (e) {
      console.warn('Failed to convert resource:', res.value, e);
      return res;
    }
  };

  const updatedRoot = await Promise.all(
    (gameDocument.resources ?? []).map((r) => convertResource(r))
  );

  const updatedPacks = gameDocument.resourcePacks
    ? await Promise.all(
        gameDocument.resourcePacks.map(async (pack) => ({
          ...pack,
          resources: pack.resources
            ? await Promise.all(pack.resources.map((r) => convertResource(r)))
            : []
        }))
      )
    : undefined;

  const updatedGameDoc: GameDocument = {
    ...gameDocument,
    resources: updatedRoot as any,
    resourcePacks: updatedPacks as any
  };

  if (current >= total) onDone?.();

  return updatedGameDoc;
};
